/**
 * CreatePlanModal — único punto de entrada para crear planes.
 *
 * Reemplaza los 3 flujos divergentes que había antes (Dashboard, Templates,
 * Clientes) — todos los caminos terminan acá. El modal:
 *  1. Exige elegir cliente (existente o crear nuevo inline). Sin cliente no
 *     se puede confirmar — adiós a los planes huérfanos.
 *  2. Permite elegir plantilla (default: "Plantilla en blanco / genérica").
 *  3. Crea el plan, precarga datos del cliente (cover, ficha, macros, medidas)
 *     y navega al editor.
 *
 * Cuando se abre, los entry points pueden pre-seleccionar:
 *  - `lockedClient` → forzar cliente (desde Clientes screen, no se puede cambiar)
 *  - `preselectedTemplate` → preseleccionar plantilla (desde Templates screen,
 *    el coach ya eligió cuál y solo le falta el cliente)
 *  - ambas vacías → entrada desde Dashboard, el coach elige todo
 */
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../../lib/Icon";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../contexts/AuthContext";
import {
  getClient,
  listClients,
  createClient,
  type ClientFull,
} from "../../api/clients";
import { listMeasurements } from "../../api/measurements";
import { createPlan, updatePlan } from "../../api/plans";
import {
  applyBrandCoverDefaults,
  brandFromUser,
  defaultPlan,
} from "../../types/plan";
import {
  TEMPLATES,
  type PlanTemplate,
} from "../../types/templates";
import {
  prefillSectionsFromClient,
  prefillMeasurementsSection,
  seedDietCalculatorFromClient,
} from "./prefillFromClient";
import { AiGeneratePlanModal } from "./AiGeneratePlanModal";
import type { PlanDoc } from "../../types/plan";
import { aiPlanDocToSections } from "../../lib/aiPlanDocMapper";

interface CreatePlanModalProps {
  onClose: () => void;
  onCreated: (planId: number) => void;
  /** Si se pasa, el modal arranca con este cliente preseleccionado y NO se
   *  puede cambiar (caso: clic "Crear plan" en card de cliente específico). */
  lockedClient?: ClientFull | null;
  /** Si se pasa, el modal arranca con esta plantilla preseleccionada. El coach
   *  puede cambiarla; sirve para el caso "vi una plantilla, la quiero usar". */
  preselectedTemplate?: PlanTemplate | null;
}

type Step = "client" | "template";

export function CreatePlanModal({
  onClose,
  onCreated,
  lockedClient = null,
  preselectedTemplate = null,
}: CreatePlanModalProps) {
  const { user } = useAuth();
  const toast = useToast();

  // ---- estado del wizard ----
  // Si viene cliente locked, arrancamos en step "template" porque el primero
  // ya está resuelto. Si no, arrancamos en "client".
  const [step, setStep] = useState<Step>(lockedClient ? "template" : "client");
  const [client, setClient] = useState<ClientFull | null>(lockedClient);
  const [template, setTemplate] = useState<PlanTemplate | null>(preselectedTemplate);
  const [creating, setCreating] = useState(false);

  // ---- lista de clientes (lazy) ----
  const [clients, setClients] = useState<ClientFull[] | null>(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsErr, setClientsErr] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (lockedClient) return; // no hace falta cargar lista
    setClientsLoading(true);
    listClients()
      .then((cs) => setClients(cs))
      .catch((e) => setClientsErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setClientsLoading(false));
  }, [lockedClient]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !creating) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, creating]);

  // ---- crear cliente inline (en step "client") ----
  const [createInline, setCreateInline] = useState(false);
  const [inlineName, setInlineName] = useState("");

  // ---- modal de generación con IA (tercera opción en step "template") ----
  const [showAiModal, setShowAiModal] = useState(false);

  const handleCreateInline = async () => {
    const name = inlineName.trim();
    if (!name) return;
    try {
      const c = await createClient({ full_name: name });
      // El nuevo cliente queda seleccionado y avanzamos al step 2.
      setClient(c);
      setClients((prev) => (prev ? [c, ...prev] : [c]));
      setCreateInline(false);
      setInlineName("");
      setStep("template");
    } catch (e) {
      toast("No pude crear el cliente: " + (e instanceof Error ? e.message : ""));
    }
  };

  // ---- filtrado de clientes ----
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    const lower = q.trim().toLowerCase();
    return clients
      .filter((c) => c.is_active !== false)
      .filter((c) => !lower || c.full_name.toLowerCase().includes(lower));
  }, [clients, q]);

  // ---- submit final ----
  const handleConfirm = async () => {
    if (!client || creating) return;
    setCreating(true);
    try {
      // Si no se eligió plantilla, usamos defaultPlan SIN applyDefaults —
      // el plan queda en blanco con la marca del coach.
      const base = defaultPlan({
        template: template?.id ?? "blank",
        clientName: client.full_name,
        brand: brandFromUser(user),
      });
      const filled = template ? template.applyDefaults(base) : base;
      applyBrandCoverDefaults(filled, user);
      filled.name = template ? `Plan ${template.label}` : `Plan ${client.full_name}`;

      const remote = await createPlan({
        name: filled.name,
        template: filled.template,
        client_id: client.id,
        // backend rellena client_name desde client_id
        client_name: null,
        sections: filled.sections,
        // Persistimos la marca del coach desde la creación — sin esto el backend
        // guardaba brand=null y el plan salía "por defecto" (bug #T2).
        brand: filled.brand,
      });

      // Precarga de datos del cliente (best-effort — si falla seguimos)
      try {
        const [full, measurements] = await Promise.all([
          getClient(client.id),
          listMeasurements(client.id),
        ]);
        prefillSectionsFromClient(filled, full, measurements);
        prefillMeasurementsSection(filled, full, measurements);
        await updatePlan(remote.id, { sections: filled.sections });
      } catch {
        // ignoramos — el coach puede completar a mano
      }

      toast(
        template
          ? `Plantilla "${template.label}" aplicada para ${client.full_name}`
          : `Plan en blanco creado para ${client.full_name}`,
      );
      onCreated(remote.id);
      onClose();
    } catch (e) {
      toast("No pude crear el plan: " + (e instanceof Error ? e.message : ""));
      setCreating(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !creating && onClose()}>
      <div
        className="al-modal al-create-plan"
        role="dialog"
        aria-label="Crear plan"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Crear plan
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              {step === "client"
                ? "Paso 1 de 2 — Elegí el cliente"
                : "Paso 2 de 2 — Elegí la plantilla"}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar" disabled={creating}>
            <Icon name="x" size={16} />
          </button>
        </div>

        {/* Barra de pasos arriba */}
        <div className="al-create-plan-steps">
          <div className={"al-create-plan-step " + (step === "client" ? "is-active" : "is-done")}>
            <span className="al-create-plan-step-num">1</span>
            <span className="al-create-plan-step-lbl">
              Cliente
              {client && step !== "client" && (
                <span className="al-create-plan-step-val"> · {client.full_name}</span>
              )}
            </span>
          </div>
          <div className="al-create-plan-step-sep" />
          <div className={"al-create-plan-step " + (step === "template" ? "is-active" : "")}>
            <span className="al-create-plan-step-num">2</span>
            <span className="al-create-plan-step-lbl">Plantilla</span>
          </div>
        </div>

        {/* Cuerpo del step */}
        {step === "client" && (
          <ClientStep
            clients={filteredClients}
            allClients={clients}
            loading={clientsLoading}
            err={clientsErr}
            q={q}
            setQ={setQ}
            onPick={(c) => { setClient(c); setStep("template"); }}
            createInline={createInline}
            setCreateInline={setCreateInline}
            inlineName={inlineName}
            setInlineName={setInlineName}
            onCreateInline={handleCreateInline}
          />
        )}

        {step === "template" && (
          <TemplateStep
            client={client!}
            selected={template}
            onSelect={setTemplate}
            canChangeClient={!lockedClient}
            onBack={() => setStep("client")}
            onPickAi={() => setShowAiModal(true)}
          />
        )}

        {/* Footer */}
        <div className="al-create-plan-foot">
          {step === "template" && !lockedClient ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setStep("client")}
              disabled={creating}
            >
              <Icon name="arrowL" size={12} /> Cambiar cliente
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={creating}
            >
              Cancelar
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step === "client" && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setStep("template")}
              disabled={!client}
            >
              Siguiente <Icon name="arrow" size={14} />
            </button>
          )}
          {step === "template" && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={!client || creating}
            >
              {creating
                ? "Creando…"
                : (template ? "Crear plan con plantilla" : "Crear plan en blanco")}
            </button>
          )}
        </div>
      </div>

      {showAiModal && client && (
        <AiGeneratePlanModal
          lockedClient={client}
          onClose={() => setShowAiModal(false)}
          onApply={async (planDoc, sourceClient) => {
            // El coach generó con IA y aceptó: creamos el plan AHORA, con la
            // marca del coach + las secciones rellenadas a partir del JSON.
            setCreating(true);
            try {
              const base = defaultPlan({
                template: "blank",
                clientName: sourceClient.full_name,
                brand: brandFromUser(user),
              });
              applyBrandCoverDefaults(base, user);
              const filled: PlanDoc = {
                ...base,
                name: `Plan ${sourceClient.full_name} ✨ IA`,
                sections: aiPlanDocToSections(base.sections, planDoc),
              };
              // La hoja de medidas se rellena por defecto desde el histórico del
              // cliente (igual que en el flujo de plantilla). El coach puede quitar
              // la sección si no la quiere. Best-effort: si falla, seguimos igual.
              try {
                const [full, measurements] = await Promise.all([
                  getClient(sourceClient.id),
                  listMeasurements(sourceClient.id),
                ]);
                prefillMeasurementsSection(filled, full, measurements);
                // Sembrar la calculadora de dieta (peso/altura/edad/sexo/objetivo)
                // para que no salga en blanco — sin tocar los macros/comidas de la IA.
                seedDietCalculatorFromClient(filled, full, measurements);
              } catch {
                // ignoramos — el coach puede completar a mano
              }
              const remote = await createPlan({
                name: filled.name,
                template: filled.template,
                client_id: sourceClient.id,
                client_name: null,
                sections: filled.sections,
                brand: filled.brand,
              });
              toast(`Plan generado con IA aplicado a ${sourceClient.full_name}`);
              onCreated(remote.id);
              onClose();
            } catch (e) {
              toast("No pude crear el plan: " + (e instanceof Error ? e.message : ""));
              setCreating(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// ClientStep — buscar/elegir cliente + crear nuevo inline
// ----------------------------------------------------------------------

interface ClientStepProps {
  clients: ClientFull[];
  allClients: ClientFull[] | null;
  loading: boolean;
  err: string | null;
  q: string;
  setQ: (v: string) => void;
  onPick: (c: ClientFull) => void;
  createInline: boolean;
  setCreateInline: (v: boolean) => void;
  inlineName: string;
  setInlineName: (v: string) => void;
  onCreateInline: () => void;
}

function ClientStep({
  clients, allClients, loading, err, q, setQ, onPick,
  createInline, setCreateInline, inlineName, setInlineName, onCreateInline,
}: ClientStepProps) {
  if (err) {
    return (
      <div className="al-create-plan-body">
        <div className="al-create-plan-err">No pude cargar los clientes: {err}</div>
      </div>
    );
  }

  if (loading && !allClients) {
    return (
      <div className="al-create-plan-body">
        <div className="muted" style={{ padding: 24, textAlign: "center" }}>Cargando clientes…</div>
      </div>
    );
  }

  return (
    <div className="al-create-plan-body">
      {createInline ? (
        <div className="al-create-plan-inline-new">
          <div className="al-create-plan-inline-hd">
            <b>Nuevo cliente</b>
            <button
              type="button"
              className="link-btn tiny"
              onClick={() => { setCreateInline(false); setInlineName(""); }}
            >
              ← Volver a la lista
            </button>
          </div>
          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Nombre completo *</span>
            <input
              type="text"
              className="al-input"
              value={inlineName}
              onChange={(e) => setInlineName(e.target.value)}
              placeholder="Ej. Camila Mayorga"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && inlineName.trim()) onCreateInline(); }}
            />
            <span className="tiny muted" style={{ marginTop: 4 }}>
              Después podrás editar todos sus datos desde Clientes.
            </span>
          </label>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onCreateInline}
            disabled={!inlineName.trim()}
            style={{ alignSelf: "flex-start" }}
          >
            <Icon name="plus" size={12} /> Crear cliente y continuar
          </button>
        </div>
      ) : (
        <>
          <div className="al-create-plan-search">
            <Icon name="search" size={14} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar cliente por nombre…"
              autoFocus
            />
          </div>

          {allClients && allClients.length === 0 ? (
            <div className="al-create-plan-empty">
              <div className="muted" style={{ marginBottom: 12 }}>
                Todavía no tenés clientes. Creá uno para empezar.
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setCreateInline(true)}
              >
                <Icon name="plus" size={12} /> Crear primer cliente
              </button>
            </div>
          ) : (
            <>
              <div className="al-create-plan-clients">
                {clients.length === 0 ? (
                  <div className="muted" style={{ padding: 16, textAlign: "center" }}>
                    Sin resultados para "{q}"
                  </div>
                ) : (
                  clients.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="al-create-plan-client-row"
                      onClick={() => onPick(c)}
                    >
                      <div className="al-create-plan-client-avatar">
                        {(c.full_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="al-create-plan-client-info">
                        <div className="al-create-plan-client-name">{c.full_name}</div>
                        <div className="tiny muted">
                          {c.sex === "F" ? "Mujer" : c.sex === "M" ? "Hombre" : "—"}
                          {c.current_weight_kg != null ? ` · ${c.current_weight_kg} kg` : ""}
                          {c.goal ? ` · ${c.goal}` : ""}
                          {c.plans_count > 0 ? ` · ${c.plans_count} plan${c.plans_count === 1 ? "" : "es"}` : ""}
                        </div>
                      </div>
                      <Icon name="arrow" size={14} />
                    </button>
                  ))
                )}
              </div>
              <button
                type="button"
                className="al-create-plan-add-client"
                onClick={() => setCreateInline(true)}
              >
                <Icon name="plus" size={12} /> Crear cliente nuevo
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// TemplateStep — galería compacta de plantillas + "en blanco"
// ----------------------------------------------------------------------

interface TemplateStepProps {
  client: ClientFull;
  selected: PlanTemplate | null;
  onSelect: (t: PlanTemplate | null) => void;
  canChangeClient: boolean;
  onBack: () => void;
  /** Abre el modal de generación con IA — tercera opción (además de
   *  Plantilla en blanco / preset). */
  onPickAi: () => void;
}

function TemplateStep({ client, selected, onSelect, onPickAi }: TemplateStepProps) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const lower = q.trim().toLowerCase();
    if (!lower) return TEMPLATES;
    return TEMPLATES.filter((t) =>
      `${t.label} ${t.category} ${t.description}`.toLowerCase().includes(lower),
    );
  }, [q]);

  return (
    <div className="al-create-plan-body">
      <div className="al-create-plan-tpl-context">
        <Icon name="user" size={12} /> Plan para <b>&nbsp;{client.full_name}</b>
      </div>

      <div className="al-create-plan-search">
        <Icon name="search" size={14} />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar plantilla — hipertrofia, definición, calistenia…"
        />
      </div>

      <div className="al-create-plan-templates">
        {/* Tercera opción: Generar con IA. Abre modal aparte que confirma el
            plan ANTES de crearlo — el modal se encarga de createPlan. */}
        <button
          type="button"
          className="al-create-plan-tpl al-create-plan-tpl-ai"
          onClick={onPickAi}
          title="Genera el plan completo (rutina + dieta + cardio + suplementos) con Claude Haiku"
        >
          <div className="al-create-plan-tpl-ai-mark">✨</div>
          <div className="al-create-plan-tpl-info">
            <div className="al-create-plan-tpl-name">
              Generar con IA
              <span className="al-create-plan-tpl-badge">NUEVO</span>
            </div>
            <div className="tiny muted">
              Claude arma rutina, dieta, cardio y suplementos en segundos — vos lo revisás antes de aplicar.
            </div>
          </div>
          <Icon name="arrow" size={14} />
        </button>

        {/* Opción "blank" siempre primero — fallback si no querés un preset */}
        <button
          type="button"
          className={"al-create-plan-tpl al-create-plan-tpl-blank" + (selected == null ? " is-selected" : "")}
          onClick={() => onSelect(null)}
        >
          <div className="al-create-plan-tpl-blank-mark">+</div>
          <div className="al-create-plan-tpl-info">
            <div className="al-create-plan-tpl-name">Plan en blanco</div>
            <div className="tiny muted">
              Sin contenido predefinido — armás todo desde cero con la marca del coach.
            </div>
          </div>
          {selected == null && <Icon name="check" size={14} />}
        </button>

        {list.map((t) => {
          const isOn = selected?.id === t.id;
          return (
            <button
              key={t.id}
              type="button"
              className={"al-create-plan-tpl" + (isOn ? " is-selected" : "")}
              onClick={() => onSelect(t)}
              style={{
                borderColor: isOn ? t.palette.primary : undefined,
              }}
            >
              <div
                className="al-create-plan-tpl-swatch"
                style={{ background: t.palette.primary, color: "#fff" }}
              >
                {t.label.slice(0, 2).toUpperCase()}
              </div>
              <div className="al-create-plan-tpl-info">
                <div className="al-create-plan-tpl-name">{t.label}</div>
                <div className="tiny muted">
                  <b style={{ color: t.palette.primary }}>{t.category}</b> · {t.description}
                </div>
              </div>
              {isOn && <Icon name="check" size={14} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
